import React from 'react';
import { X } from 'lucide-react';
import type { Applicant } from '../../types/applicant';
import { useClickOutside } from '../../hooks/useClickOutside';

interface ApplicantDetailsModalProps {
  applicant: Applicant;
  onClose: () => void;
}

export default function ApplicantDetailsModal({ applicant, onClose }: ApplicantDetailsModalProps) {
  const modalContentRef = React.useRef<HTMLDivElement>(null);
  useClickOutside(modalContentRef, onClose);

  React.useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start md:items-center justify-center p-0 z-[100] overflow-y-auto"
    >
      <div 
        ref={modalContentRef}
        className="relative bg-white w-full md:max-w-2xl md:rounded-lg md:my-8 min-h-screen md:min-h-0 max-h-full md:max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex justify-between items-center p-4 md:p-6 border-b bg-white">
          <h3 className="text-xl font-semibold text-gray-900">Applicant Details</h3>
          <button 
            onClick={onClose}
            className="rounded-full p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content - with scrollable area */}
        <div className="p-4 md:p-6 space-y-6 overflow-y-auto flex-1">
          {/* Basic Information */}
          <div className="border-b pb-4">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Name</label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {applicant.appl_name}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">ID</label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {applicant.appl_id}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Abbreviation</label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {applicant.appl_abbr || 'Not specified'}
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h4 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Contact Name</label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {applicant.appl_cont_name || 'Not specified'}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Phone</label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {applicant.appl_phone || 'Not specified'}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Email</label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {applicant.appl_email || 'Not specified'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 z-10 flex justify-end p-4 md:p-6 border-t bg-white">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 border border-gray-300 rounded-md"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
